import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  VerifiedUser as QualityIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CompleteIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { 
  DialogContainer,
  DialogTitle, 
  DialogBody, 
  DialogActions,
  CustomCheckbox,
  CustomTextField,
  CustomSelect,
  LoadingSkeleton 
} from '../../../../common/components';
import { 
  useProjectQualityTracking,
  useQualitySummary,
  useBulkUpdateQuality 
} from '../../../../hooks/useProjectQualityTracking';

const deriveQualityStatus = (percentage, failed = false) => {
  if (failed) return 'failed';
  const value = Number(percentage) || 0;
  if (value >= 100) return 'completed';
  if (value > 0) return 'in_progress';
  return 'not_started';
};

/**
 * Modal de control de calidad del proyecto
 * Migrado desde controlCalidadURL.yml siguiendo patrones del proyecto
 */
const ProjectQualityDialog = ({ open, onClose, project }) => {
  const [qualityUpdates, setQualityUpdates] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedPhase, setExpandedPhase] = useState(null);

  const { data: qualityData, isLoading } = useProjectQualityTracking(project?.uuid, { enabled: open && !!project });
  const { data: summaryData } = useQualitySummary(project?.uuid, { enabled: open && !!project });
  const bulkUpdateMutation = useBulkUpdateQuality();

  const statusOptions = [
    { value: 'not_started', label: 'Sin empezar', color: 'default', icon: PendingIcon },
    { value: 'in_progress', label: 'En progreso', color: 'warning', icon: WarningIcon },
    { value: 'completed', label: 'Terminado', color: 'success', icon: CompleteIcon },
    { value: 'failed', label: 'Fallido', color: 'error', icon: ErrorIcon },
  ];

  const severityOptions = [
    { value: 'low', label: 'Baja', color: 'success' },
    { value: 'medium', label: 'Media', color: 'warning' },
    { value: 'high', label: 'Alta', color: 'error' },
  ];

  useEffect(() => {
    if (qualityData && open) {
      const initialValues = {};
      qualityData.forEach(item => {
        const percentage = parseFloat(item.percentage) || 0;
        const status = deriveQualityStatus(percentage, item.status === 'failed');

        initialValues[item.uuid_task] = {
          status,
          observations: item.observations || '',
          severity: item.severity || 'low',
          control_date: item.control_date || null,
          original_status: status,
          original_observations: item.observations || '',
          original_severity: item.severity || 'low',
          task_name: item.task?.name || 'Control sin nombre',
          phase: item.phase || 'General',
          percentage,
        };
      });
      setQualityUpdates(initialValues);
      setHasChanges(false);
    }
  }, [qualityData, open]);

  const handleFieldChange = (taskId, field, value) => {
    setQualityUpdates(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value,
        ...(field === 'status' && value === 'failed' && {
          control_date: new Date().toISOString().split('T')[0]
        })
      }
    }));
    setHasChanges(true);
  };

  const groupByPhase = () => {
    const phases = {};
    Object.entries(qualityUpdates).forEach(([taskId, data]) => {
      const phase = data.phase;
      if (!phases[phase]) {
        phases[phase] = [];
      }
      phases[phase].push({ taskId, ...data });
    });
    return phases;
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    const updates = Object.entries(qualityUpdates)
      .filter(([taskId, data]) => 
        data.status !== data.original_status ||
        data.observations !== data.original_observations ||
        data.severity !== data.original_severity
      )
      .map(([taskId, data]) => ({
        uuid_task: taskId,
        status: deriveQualityStatus(data.percentage, data.status === 'failed'),
        observations: data.observations.trim() || null,
        severity: data.severity,
        ...(data.control_date && { control_date: data.control_date })
      }));

    if (updates.length > 0) {
      try {
        await bulkUpdateMutation.mutateAsync({
          projectId: project.uuid,
          qualityUpdates: updates
        });
        setHasChanges(false);
        onClose();
      } catch (error) {
        console.error('Error al actualizar controles de calidad:', error);
      }
    }
  };

  const handleClose = () => {
    setHasChanges(false);
    setQualityUpdates({});
    onClose();
  };

  const getStatusDisplay = (status) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option || statusOptions[0];
  };

  const calculatePhaseStats = (tasks) => {
    const total = tasks.length;
    const completed = tasks.filter(t => deriveQualityStatus(t.percentage, t.status === 'failed') === 'completed').length;
    const inProgress = tasks.filter(t => deriveQualityStatus(t.percentage, t.status === 'failed') === 'in_progress').length;
    const failed = tasks.filter(t => deriveQualityStatus(t.percentage, t.status === 'failed') === 'failed').length;
    const notStarted = tasks.filter(t => deriveQualityStatus(t.percentage, t.status === 'failed') === 'not_started').length;

    return {
      total,
      completed,
      inProgress,
      failed,
      notStarted,
      completionPercentage: total > 0 ? (completed / total) * 100 : 0
    };
  };

  const phasesData = groupByPhase();

  return (
    <DialogContainer open={open} onClose={handleClose} size="fullWidth">
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <QualityIcon color="primary" />
          Control de Calidad - {project?.name}
        </Box>
      </DialogTitle>

      <DialogBody>
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <Box>
            {summaryData && (
              <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Resumen General de Calidad
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Total de Controles
                      </Typography>
                      <Typography variant="h6">{summaryData.total_controls}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Completados
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {summaryData.completed_controls}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Progreso General
                      </Typography>
                      <Typography variant="h6">
                        {summaryData.overall_completion_percentage}%
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Estado
                      </Typography>
                      <Chip 
                        label={hasChanges ? 'Cambios pendientes' : 'Sincronizado'}
                        color={hasChanges ? 'warning' : 'success'}
                        variant={hasChanges ? 'filled' : 'outlined'}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {hasChanges && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Tienes cambios sin guardar en los controles de calidad.
              </Alert>
            )}

            <Box>
              {Object.entries(phasesData).map(([phase, tasks]) => {
                const phaseStats = calculatePhaseStats(tasks);
                return (
                  <Accordion 
                    key={phase}
                    expanded={expandedPhase === phase}
                    onChange={(e, isExpanded) => setExpandedPhase(isExpanded ? phase : null)}
                    sx={{ mb: 2 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                        <Typography variant="h6">
                          Fase: {phase} ({tasks.length} controles)
                        </Typography>
                        <Box display="flex" gap={1} sx={{ mr: 2 }}>
                          <Chip 
                            label={`${phaseStats.completionPercentage.toFixed(0)}% completado`}
                            color={phaseStats.completionPercentage === 100 ? 'success' : 'primary'}
                            size="small"
                          />
                          {phaseStats.failed > 0 && (
                            <Chip 
                              label={`${phaseStats.failed} fallidos`}
                              color="error"
                              size="small"
                            />
                          )}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {tasks.map(({ taskId, task_name, status, observations, severity, percentage }) => {
                          const statusDisplay = getStatusDisplay(status);
                          const StatusIcon = statusDisplay.icon;

                          return (
                            <Grid item xs={12} key={taskId}>
                              <Card elevation={1}>
                                <CardContent>
                                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Typography variant="h6" gutterBottom>
                                      {task_name}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <StatusIcon color={statusDisplay.color} fontSize="small" />
                                      <Typography variant="body2" color="text.secondary">
                                        {percentage}%
                                      </Typography>
                                    </Box>
                                  </Box>

                                  <Grid container spacing={3}>
                                    <Grid item xs={12} sm={4}>
                                      <Box display="flex" alignItems="center" height="56px">
                                        <CustomCheckbox
                                          label="Fallido"
                                          checked={status === 'failed'}
                                          onChange={(e) =>
                                            handleFieldChange(
                                              taskId,
                                              'status',
                                              e.target.checked
                                                ? 'failed'
                                                : deriveQualityStatus(percentage, false)
                                            )
                                          }
                                          labelSx={{ fontWeight: 700 }}
                                        />
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                      <CustomSelect
                                        label="Severidad"
                                        name="severity"
                                        value={severity}
                                        onChange={(e) => handleFieldChange(taskId, 'severity', e.target.value)}
                                        size="medium"
                                      >
                                        {severityOptions.map(option => (
                                          <MenuItem key={option.value} value={option.value}>
                                            <Chip 
                                              label={option.label} 
                                              color={option.color} 
                                              size="small" 
                                              variant="outlined"
                                            />
                                          </MenuItem>
                                        ))}
                                      </CustomSelect>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                      <Box display="flex" justifyContent="flex-end" alignItems="center" height="56px">
                                        {(qualityUpdates[taskId]?.status !== qualityUpdates[taskId]?.original_status ||
                                          qualityUpdates[taskId]?.observations !== qualityUpdates[taskId]?.original_observations ||
                                          qualityUpdates[taskId]?.severity !== qualityUpdates[taskId]?.original_severity) && (
                                          <Chip 
                                            label="Modificado" 
                                            color="warning" 
                                            size="small" 
                                            variant="outlined"
                                          />
                                        )}
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                      <CustomTextField
                                        label="Observaciones"
                                        name="observations"
                                        value={observations}
                                        onChange={(e) => handleFieldChange(taskId, 'observations', e.target.value)}
                                        size="medium"
                                        multiline
                                        rows={3}
                                      />
                                    </Grid>
                                  </Grid>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogBody>

      <DialogActions 
        handleClose={handleClose}
        handleSave={handleSave}
        saveDisabled={!hasChanges || bulkUpdateMutation.isPending}
        saveIcon={SaveIcon}
        saveText={bulkUpdateMutation.isPending ? 'Guardando...' : 'Guardar Cambios'}
      />
    </DialogContainer>
  );
};

export default ProjectQualityDialog;
